import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Gantt, Willow } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/style.css';
import type { ITask, IScaleConfig } from '@svar-ui/react-gantt';
import { listProjects, ProjectWithDetails } from '../services/projectService';
import Layout from '../components/Layout';

type ViewMode = 'Week' | 'Month' | 'Quarter' | 'Year';

const formatQuarter = (date: Date) => `Q${Math.floor(date.getMonth() / 3) + 1}`;

const scaleConfigs: Record<ViewMode, IScaleConfig[]> = {
  Week: [
    { unit: 'month', step: 1, format: '%F %Y' },
    { unit: 'week', step: 1, format: 'Week %W' },
  ],
  Month: [
    { unit: 'year', step: 1, format: '%Y' },
    { unit: 'month', step: 1, format: '%F' },
  ],
  Quarter: [
    { unit: 'year', step: 1, format: '%Y' },
    { unit: 'quarter', step: 1, format: formatQuarter },
  ],
  Year: [
    { unit: 'year', step: 1, format: '%Y' },
    { unit: 'quarter', step: 1, format: formatQuarter },
  ],
};

const columns = [
  { id: 'text', header: 'Project Name', flexgrow: 1 },
  {
    id: 'start',
    header: 'Start',
    align: 'center' as const,
    width: 110,
    template: (v: Date) => {
      if (!v) return '';
      const m = String(v.getMonth() + 1).padStart(2, '0');
      const d = String(v.getDate()).padStart(2, '0');
      return `${m}-${d}-${v.getFullYear()}`;
    },
  },
  { id: 'duration', header: 'Days', align: 'center' as const, width: 70 },
];

function statusToProgress(status: string): number {
  switch (status) {
    case 'done': return 100;
    case 'in_progress': return 50;
    case 'ready': return 25;
    default: return 0;
  }
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  ready: 'Ready',
  in_progress: 'In Progress',
  done: 'Done',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  ready: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  in_progress: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
  done: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
};

const ProgramGantt: React.FC = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('Quarter');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [unscheduledExpanded, setUnscheduledExpanded] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!token) return;
      try {
        setLoading(true);
        setError(null);
        const result = await listProjects({}, token);
        setProjects(result.projects || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [token]);

  const ownerNames = useMemo(() => {
    const names = new Set<string>();
    projects.forEach(p => { if (p.owner_name) names.add(p.owner_name); });
    return Array.from(names).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (ownerFilter === 'all') return projects;
    return projects.filter(p => p.owner_name === ownerFilter);
  }, [projects, ownerFilter]);

  const scheduledProjects = useMemo(
    () => filteredProjects.filter(p => p.start_date && p.target_completion_date),
    [filteredProjects]
  );

  const unscheduledProjects = useMemo(
    () => filteredProjects.filter(p => !p.start_date || !p.target_completion_date),
    [filteredProjects]
  );

  const tasks: ITask[] = useMemo(() => {
    const items = scheduledProjects.map(project => {
      const start = new Date(project.start_date!);
      const end = new Date(project.target_completion_date!);
      const duration = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
      return {
        id: project.id,
        text: project.name,
        start,
        end,
        duration,
        progress: statusToProgress(project.status),
        type: 'task' as const,
      };
    });
    // Spacer row so the last real bar isn't flush against the bottom edge
    if (items.length > 0) {
      items.push({ id: '_spacer', text: '', start: new Date(), end: new Date(), duration: 0, progress: 0, type: 'task' });
    }
    return items;
  }, [scheduledProjects]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <div className="text-gray-500 dark:text-gray-400">Loading program timeline...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-white">Program Gantt</h1>
          <p className="text-blue-100 mt-1">Cross-project timeline view &mdash; all company projects at a glance</p>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* View mode switcher */}
        <div className="flex items-center gap-2 mb-4">
          {(['Week', 'Month', 'Quarter', 'Year'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === mode
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {mode}
            </button>
          ))}

          <select
            value={ownerFilter}
            onChange={e => setOwnerFilter(e.target.value)}
            className="ml-4 pl-3 pr-8 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Owners</option>
            {ownerNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
            {scheduledProjects.length} of {filteredProjects.length} projects scheduled
          </span>
        </div>

        {/* Gantt Chart */}
        {tasks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex items-center justify-center py-20">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No scheduled projects</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Projects need both a start date and target completion date to appear on the timeline.
              </p>
            </div>
          </div>
        ) : (
          <div
            className="svar-gantt-wrapper bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            style={{ height: Math.max(200, tasks.length * 38 + 72 + 10) }}
          >
            <Willow>
              <Gantt
                tasks={tasks}
                links={[]}
                scales={scaleConfigs[viewMode]}
                columns={columns}
                cellHeight={38}
                scaleHeight={36}
                readonly={true}
                highlightTime={(date: Date, unit: string) => {
                  if (unit === 'day') {
                    const today = new Date();
                    if (date.getFullYear() === today.getFullYear() &&
                        date.getMonth() === today.getMonth() &&
                        date.getDate() === today.getDate()) {
                      return 'today-highlight';
                    }
                  }
                  return '';
                }}
              />
            </Willow>
          </div>
        )}

        {/* Unscheduled Projects Panel */}
        {unscheduledProjects.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mt-4">
            <button
              onClick={() => setUnscheduledExpanded(!unscheduledExpanded)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${unscheduledExpanded ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Unscheduled Projects
                </h3>
                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full">
                  {unscheduledProjects.length}
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Missing start or target completion date
              </span>
            </button>

            {unscheduledExpanded && (
              <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
                {unscheduledProjects.map(project => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {project.name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                          {project.project_code}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">&middot;</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[project.status] || STATUS_COLORS.draft}`}>
                          {STATUS_LABELS[project.status] || project.status}
                        </span>
                        {project.owner_name && (
                          <>
                            <span className="text-xs text-gray-400 dark:text-gray-500">&middot;</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{project.owner_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Override SVAR Willow theme font to match app */}
      <style>{`
        .svar-gantt-wrapper .wx-willow-theme,
        .svar-gantt-wrapper .wx-willow-dark-theme {
          --wx-font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Arial', sans-serif;
          --wx-font-size: 13px;
          --wx-font-size-md: 14px;
          --wx-font-size-sm: 11px;
          --wx-font-size-hd: 14px;
        }
        .svar-gantt-wrapper .wx-willow-theme .wx-gantt,
        .svar-gantt-wrapper .wx-willow-dark-theme .wx-gantt {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Arial', sans-serif;
          font-size: 13px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .svar-gantt-wrapper .today-highlight {
          background: rgba(239, 68, 68, 0.08);
          border-left: 2px solid #ef4444;
        }
        .svar-gantt-wrapper [data-id="_spacer"] {
          color: transparent !important;
        }
        .svar-gantt-wrapper .wx-chart {
          scrollbar-width: thin;
          scrollbar-color: rgba(0,0,0,0.15) transparent;
        }
        .svar-gantt-wrapper .wx-chart::-webkit-scrollbar {
          height: 6px;
        }
        .svar-gantt-wrapper .wx-chart::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.15);
          border-radius: 3px;
        }
        .svar-gantt-wrapper .wx-chart::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </Layout>
  );
};

export default ProgramGantt;
